// Dashboard — matches PRD v2 spec exactly:
//   5 KPI cards: Active Projects, Overdue Payments, At-Risk Projects,
//                Pipeline Value, Collected This Month
//   Active projects mini-Gantt (read-only)
//   Alerts panel (overdue / at-risk / unassigned)
//   Upcoming Payments (next 30 days)
//   Pipeline Summary
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Loader2, ChevronRight, CircleDot } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  ALLOWED_ROLES, fetchJson, fmtMoney, fmtMoneyShort, fmtDate, healthBadge,
} from "./pccHelpers";

const DAY_MS = 24 * 60 * 60 * 1000;

export default function PccDashboard() {
  const { user } = useAuth();
  const canAccess = ALLOWED_ROLES.has(user?.role);
  const navigate = useNavigate();

  const [pcc, setPcc] = useState(null);
  const [pay, setPay] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p1, p2] = await Promise.all([
        fetchJson("/planning-command-center"),
        fetchJson("/planning-command-center/payments-dashboard"),
      ]);
      setPcc(p1); setPay(p2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // ── Derived metrics for the 5 PRD KPIs ──
  const kpis = useMemo(() => {
    const projects = pcc?.projects || [];
    return {
      activeProjects: projects.length,
      overduePayments: pay?.kpis?.total_overdue || 0,
      atRisk: projects.filter(p => ["at_risk", "at-risk", "delayed"].includes((p.health || "").toLowerCase())).length,
      pipelineValue: pcc?.kpis?.pipeline_value || 0,
      collectedThisMonth: pay?.kpis?.collected_this_month || 0,
    };
  }, [pcc, pay]);

  // ── Mini-Gantt range (90 days from today) ──
  const ganttRange = useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - 30);
    return { start, days: 120 };
  }, []);
  const ganttWidth = 800;
  const pxPerDay = ganttWidth / ganttRange.days;
  const todayOffset = Math.floor((Date.now() - ganttRange.start.getTime()) / DAY_MS) * pxPerDay;

  if (!canAccess) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center p-6" data-testid="pcc-access-denied">
        <Card className="bg-[#000000] border-white/[0.08] p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h2 className="text-white text-xl font-light">Access restricted</h2>
          <p className="text-white/60 text-sm mt-2">Planning Command Center is available to PM, Production, Sales, and Executive roles.</p>
        </Card>
      </div>
    );
  }
  if (loading && !pcc) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading dashboard…</div>;

  const projects = pcc?.projects || [];
  const upcoming = (pcc?.upcoming_milestones || []).slice(0, 8);
  const unassigned = projects.filter(p => !p.pm_id);
  const atRiskProjects = projects.filter(p => ["at_risk", "at-risk", "delayed"].includes((p.health || "").toLowerCase()));

  // Pipeline summary
  const pipelineTotal = pcc?.kpis?.pipeline_value || 0;

  return (
    <div data-testid="pcc-dashboard">
      {/* Topbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="text-xl font-light tracking-tight text-white">Dashboard</h2>
          <p className="text-white/55 text-sm mt-1">Executive ops view — health, capacity, money, scope.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-white/40">{pcc?.generated_at ? `Updated ${new Date(pcc.generated_at).toLocaleTimeString()}` : ""}</span>
          <Button variant="outline" size="sm" onClick={refresh} className="border-white/15 bg-white/[0.04]" data-testid="pcc-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* 5 KPI Cards — PRD spec */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="pcc-kpi-strip">
        <KpiCard label="Active Projects" value={kpis.activeProjects} kind="count" />
        <KpiCard label="Overdue Payments" value={kpis.overduePayments} kind="money" tone="red" />
        <KpiCard label="At-Risk Projects" value={kpis.atRisk} kind="count" tone={kpis.atRisk > 0 ? "amber" : "white"} />
        <KpiCard label="Pipeline Value" value={kpis.pipelineValue} kind="money" tone="blue" />
        <KpiCard label="Collected This Month" value={kpis.collectedThisMonth} kind="money" tone="emerald" />
      </div>

      {/* Active projects mini-Gantt — read-only timeline strip */}
      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden mb-5" data-testid="pcc-mini-gantt">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.08]">
          <div className="flex items-center gap-2.5">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
            <div className="text-[12px] uppercase tracking-[0.08em] font-semibold text-white">Active Projects Timeline</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/planning-command-center/gantt")} className="text-amber-300 text-[11px] h-7">
            Open Gantt <ChevronRight className="w-3 h-3 ml-0.5" />
          </Button>
        </div>
        <div className="p-4 space-y-1.5 max-h-[280px] overflow-y-auto">
          {projects.slice(0, 10).map(p => {
            const sd = p.start_date ? new Date(p.start_date).getTime() : null;
            const ed = p.end_date ? new Date(p.end_date).getTime() : null;
            const left = sd != null ? Math.max(0, ((sd - ganttRange.start.getTime()) / DAY_MS) * pxPerDay) : 0;
            const width = sd && ed && ed > sd ? Math.max(8, ((ed - sd) / DAY_MS) * pxPerDay) : 24;
            const hb = healthBadge(p.health);
            return (
              <div key={p.id} className="grid items-center gap-3" style={{ gridTemplateColumns: "180px 1fr 64px" }}>
                <button onClick={() => navigate(`/planning-command-center/projects/${p.id}`)} className="text-[12px] text-white text-left truncate hover:text-amber-300">{p.name}</button>
                <div className="relative h-3 bg-white/[0.03] rounded" style={{ width: ganttWidth }}>
                  <div className="absolute top-0 bottom-0 border-l border-amber-400/40" style={{ left: todayOffset }} />
                  <div className={`absolute rounded ${p.health === "delayed" ? "bg-red-400/50" : p.health === "at_risk" ? "bg-amber-400/50" : "bg-emerald-400/50"}`} style={{ left, width, top: 1, bottom: 1 }} title={`${fmtDate(p.start_date)} → ${fmtDate(p.end_date)}`} />
                </div>
                <Badge className={`text-[9px] border ${hb.cls}`}>{hb.label.replace(/[▲●]\s*/, "")}</Badge>
              </div>
            );
          })}
          {projects.length === 0 && <div className="text-white/40 text-sm text-center py-6">No active projects.</div>}
        </div>
      </Card>

      <div className="grid gap-5" style={{ gridTemplateColumns: "1fr 380px" }}>
        {/* Left: Alerts panel */}
        <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-alerts">
          <div className="px-5 py-3 border-b border-white/[0.08] flex items-center gap-2.5">
            <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
            <div className="text-[12px] uppercase tracking-[0.08em] font-semibold text-white">Alerts</div>
          </div>
          <div className="p-5 space-y-4">
            {(pcc?.overdue_commitments || []).length > 0 && (
              <AlertGroup title="Overdue Payments" tone="red" items={pcc.overdue_commitments.slice(0, 5).map(o => ({
                title: `${fmtMoney(o.amount)} · ${o.title || "Milestone"}`,
                subtitle: `${o.days_overdue}d overdue · ${fmtDate(o.due_date)}`,
                onClick: () => { const proj = projects.find(p => p.id === o.program_id); if (proj) navigate(`/planning-command-center/projects/${proj.id}`); },
              }))} />
            )}
            {atRiskProjects.length > 0 && (
              <AlertGroup title="At-Risk Projects" tone="amber" items={atRiskProjects.slice(0, 5).map(p => ({
                title: p.name,
                subtitle: `${p.client} · ${p.health}`,
                onClick: () => navigate(`/planning-command-center/projects/${p.id}`),
              }))} />
            )}
            {unassigned.length > 0 && (
              <AlertGroup title="Unassigned Projects" tone="white" items={unassigned.slice(0, 5).map(p => ({
                title: p.name,
                subtitle: `${p.client} · No PM`,
                onClick: () => navigate(`/planning-command-center/projects/${p.id}`),
              }))} />
            )}
            {(pcc?.overdue_commitments || []).length === 0 && atRiskProjects.length === 0 && unassigned.length === 0 && (
              <div className="text-white/40 text-sm text-center py-6">No alerts. Everything is on track.</div>
            )}
          </div>
        </Card>

        {/* Right rail: Upcoming Payments + Pipeline Summary */}
        <div className="space-y-5">
          <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-upcoming-payments">
            <div className="px-5 py-3 border-b border-white/[0.08] flex items-center gap-2.5">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
              <div className="text-[12px] uppercase tracking-[0.08em] font-semibold text-white">Upcoming Payments (30d)</div>
            </div>
            <div className="px-5 max-h-[300px] overflow-y-auto">
              {upcoming.length === 0 ? <div className="py-5 text-[12px] text-white/40">No upcoming.</div> : upcoming.map((m, i) => {
                const proj = projects.find(p => p.id === m.program_id);
                return (
                  <div key={m.id} onClick={() => proj && navigate(`/planning-command-center/projects/${proj.id}`)} className={`py-2.5 cursor-pointer hover:bg-white/[0.02] -mx-5 px-5 ${i === upcoming.length - 1 ? "" : "border-b border-white/[0.06]"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] text-white truncate">{m.title || "Milestone"}</span>
                      <span className="text-[13px] tabular-nums font-mono text-amber-300">{fmtMoney(m.amount)}</span>
                    </div>
                    <div className="text-[11px] text-white/45 font-mono truncate">{proj?.name || "—"} · {fmtDate(m.due_date)}</div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden" data-testid="pcc-pipeline-summary">
            <div className="px-5 py-3 border-b border-white/[0.08] flex items-center gap-2.5">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
              <div className="text-[12px] uppercase tracking-[0.08em] font-semibold text-white">Pipeline Summary</div>
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-wider text-white/40">Total Value</div>
              <div className="text-2xl font-light text-blue-300 tabular-nums mt-1">{fmtMoney(pipelineTotal)}</div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/planning-command-center/sales")} className="mt-3 text-amber-300 text-[11px] h-7 px-0">
                Open Sales Bridge <ChevronRight className="w-3 h-3 ml-0.5" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── KPI Card ───────────────────────────────────────────────────────────
function KpiCard({ label, value, kind, tone = "white" }) {
  const toneCls = {
    white: "text-white", emerald: "text-emerald-300",
    amber: "text-amber-300", red: "text-red-300", blue: "text-blue-300",
  }[tone];
  return (
    <Card className="bg-[#000000] border-white/[0.08] p-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className={`mt-2 text-2xl font-light tabular-nums ${toneCls}`}>
        {kind === "money" ? fmtMoney(value) : Number(value || 0).toLocaleString()}
      </div>
    </Card>
  );
}

// ── Alert Group ───────────────────────────────────────────────────────
function AlertGroup({ title, tone, items }) {
  const toneCls = tone === "red" ? "text-red-300" : tone === "amber" ? "text-amber-300" : "text-white/65";
  const dotCls = tone === "red" ? "bg-red-400" : tone === "amber" ? "bg-amber-400" : "bg-white/40";
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold mb-2 ${toneCls}`}>{title}</div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} onClick={it.onClick} className="flex items-start gap-2.5 py-1.5 px-2 -mx-2 rounded cursor-pointer hover:bg-white/[0.03]">
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${dotCls}`} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white truncate">{it.title}</div>
              <div className="text-[11px] text-white/45 font-mono truncate">{it.subtitle}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
